"use client";

import { Box, Stat, Grid, Tooltip, VStack } from "@chakra-ui/react";
import { useReportStatsQuery } from "@/features/profile";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import LoadingState from "@/shared/ui/LoadingState";

type ProfileStatInfoTipProps = {
  content: string;
};

function ProfileStatInfoTip({ content }: ProfileStatInfoTipProps) {
  return (
    <Tooltip.Root openDelay={300}>
      <Tooltip.Trigger asChild>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          color="moveon.shell.mutedFg"
          cursor="help"
          aria-label="Show statistic info"
          ms="1"
        >
          <GenericIcon icon="inform" size="xs" />
        </Box>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content>{content}</Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  );
}

export default function ProfileStats() {
  const { stats, isLoading, error } = useReportStatsQuery();

  if (isLoading) {
    return <LoadingState message="Loading profile statistics..." />;
  }

  if (error || !stats) {
    return (
      <GenericEmptyComponent
        title="Could not load profile stats"
        message={error || "Unable to load stats right now."}
      />
    );
  }

  const formatNumber = (num?: number | null) => {
    if (num === null || num === undefined) return "0";
    return num.toLocaleString();
  };

  return (
    <VStack align="stretch" gap="4">
      <GenericDrawerInfo
        description="A quick health check for source material, situations, thoughts, and reflection coverage in your profile."
        items={[
          {
            label: `${formatNumber(stats.count_of_situations)} situations`,
            colorPalette: "blue",
          },
          {
            label: `${formatNumber(stats.total_thoughts)} thoughts`,
            colorPalette: "purple",
          },
          {
            label: `${formatNumber(stats.four_dim_reflection)} fully reflected`,
            colorPalette: "green",
          },
        ]}
      />

      <Grid
        templateColumns={{
          base: "repeat(1, 1fr)",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
        }}
        gap={6}
        width="100%"
      >
      {/* Situations */}
      <Stat.Root>
        <Stat.Label>
          Situations
          <ProfileStatInfoTip content="Total number of career situations you've documented" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.count_of_situations)}
        </Stat.ValueText>
      </Stat.Root>

      {/* Total Thoughts */}
      <Stat.Root>
        <Stat.Label>
          Total Thoughts
          <ProfileStatInfoTip content="All thoughts across your career profile" />
        </Stat.Label>
        <Stat.ValueText>{formatNumber(stats.total_thoughts)}</Stat.ValueText>
      </Stat.Root>

      {/* Confirmed Thoughts */}
      <Stat.Root>
        <Stat.Label>
          Confirmed
          <ProfileStatInfoTip content="Thoughts that have been reviewed and confirmed" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.confirmed_thoughts)}
        </Stat.ValueText>
      </Stat.Root>

      {/* Unconfirmed Thoughts */}
      <Stat.Root>
        <Stat.Label>
          Pending
          <ProfileStatInfoTip content="Thoughts awaiting confirmation from backlog" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.unconfirmed_thoughts)}
        </Stat.ValueText>
      </Stat.Root>

      {/* No Reflection */}
      <Stat.Root>
        <Stat.Label>
          No Reflection
          <ProfileStatInfoTip content="Thoughts without any dimensional reflections yet" />
        </Stat.Label>
        <Stat.ValueText>{formatNumber(stats.no_reflection)}</Stat.ValueText>
      </Stat.Root>

      {/* 1D Reflections */}
      <Stat.Root>
        <Stat.Label>
          1D Reflections
          <ProfileStatInfoTip content="Thoughts with one dimensional reflection" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.one_dim_reflection)}
        </Stat.ValueText>
      </Stat.Root>

      {/* 2D Reflections */}
      <Stat.Root>
        <Stat.Label>
          2D Reflections
          <ProfileStatInfoTip content="Thoughts with two dimensional reflections" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.two_dim_reflection)}
        </Stat.ValueText>
      </Stat.Root>

      {/* 3D Reflections */}
      <Stat.Root>
        <Stat.Label>
          3D Reflections
          <ProfileStatInfoTip content="Thoughts with three dimensional reflections" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.three_dim_reflection)}
        </Stat.ValueText>
      </Stat.Root>

      {/* 4D Reflections */}
      <Stat.Root>
        <Stat.Label>
          4D Reflections
          <ProfileStatInfoTip content="Thoughts with four dimensional reflections (complete)" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.four_dim_reflection)}
        </Stat.ValueText>
      </Stat.Root>

      {/* Processed Origins */}
      <Stat.Root>
        <Stat.Label>
          Processed Origins
          <ProfileStatInfoTip content="Number of sources that have been processed" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.count_of_processed_origins)}
        </Stat.ValueText>
      </Stat.Root>

      {/* Unprocessed Origins */}
      <Stat.Root>
        <Stat.Label>
          Pending Origins
          <ProfileStatInfoTip content="Sources waiting to be processed" />
        </Stat.Label>
        <Stat.ValueText>
          {formatNumber(stats.count_of_not_processed_origins)}
        </Stat.ValueText>
      </Stat.Root>
      </Grid>
    </VStack>
  );
}
